import { ChangeDetectorRef, Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MovementsService } from 'src/app/services/movements.service';
import { ToastService } from '../../../toast/toast.service';
import { IbanValidator, conditionalValidator } from './money-out-change-account.validator';
@Component({
  selector: 'app-money-out-change-account',
  templateUrl: './money-out-change-account.component.html',
  styleUrls: ['./money-out-change-account.component.scss'],
})
export class MoneyOutChangeAccountComponent implements OnInit {
  @Output() public reopenModalEvent = new EventEmitter<any>();
  @Input() public walletId: any;
  @Input() public profile: any;

  public successOperation = false;
  public bankForm: UntypedFormGroup;
  public formControlKeys = [];
  public errorMessages = {};
  public isSubmit = false;
  public sendXHR = 'primaryDark-disabled';
  public isLoading = false;

  countryList = [];

  constructor(
    private chdRef: ChangeDetectorRef,
    private toastService: ToastService,
    private translate: TranslateService,
    private movementsService: MovementsService,
  ) { }

  get isIndividual(): boolean {
    return this.profile?.profileType === 1;
  }

  ngOnInit() {
    this.countryList = this.getCountryList();
    this.bankForm = new UntypedFormGroup({
      displayName: new UntypedFormControl('', [Validators.required]),
      countryCode: new UntypedFormControl('ES', [Validators.required]),
      iban: new UntypedFormControl('', [Validators.required, IbanValidator]),
      // Individual fields
      holderName: new UntypedFormControl('', [
        conditionalValidator(
          () => this.isIndividual,
          Validators.required,
          Validators.pattern('^[A-Za-zñÑáéíóúÁÉÍÓÚ ]+$')
        )
      ]),
      holderLastName: new UntypedFormControl('', [
        conditionalValidator(
          () => this.isIndividual,
          Validators.required,
          Validators.pattern('^[A-Za-zñÑáéíóúÁÉÍÓÚ ]+$')
        )
      ]),
      // Business field
      businessName: new UntypedFormControl('', [
        conditionalValidator(
          () => !this.isIndividual,
          Validators.required
        )
      ]),
      // Address (both)
      addressLine1: new UntypedFormControl('', [
        Validators.required,
        Validators.pattern("^[A-Za-z0-9ñÑáéíóúàèìòùäëïöüÁÉÍÓÚÀÈÌÒÙÄËÏÖÜ ]+$")
      ]),
      city: new UntypedFormControl('', [
        Validators.required,
        Validators.pattern("^[A-Za-z0-9ñÑáéíóúàèìòùäëïöüÁÉÍÓÚÀÈÌÒÙÄËÏÖÜ ]+$")
      ]),
      region: new UntypedFormControl('', [
        Validators.required,
        Validators.pattern("^[A-Za-z0-9ñÑáéíóúàèìòùäëïöüÁÉÍÓÚÀÈÌÒÙÄËÏÖÜ ]+$")
      ]),
      postalCode: new UntypedFormControl('', [Validators.required]),
    });

    this.formControlKeys = Object.keys(this.bankForm.controls);
    this.errorMessages = {
      required: this.translate.instant('input-errors-messages.required'),
      pattern: this.translate.instant('input-errors-messages.format'),
      code: this.translate.instant('MOVEMENTS.MONEY-OUT.IBAN_ERROR'),
    };
    this.chdRef.detectChanges();
  }

  @HostListener('document:keyup') onKeyup() {
    this.controlForm();
    this.chdRef.detectChanges();
  }

  controlForm() {
    this.sendXHR = this.bankForm.valid ? 'primaryDark' : 'primaryDark-disabled';
    this.chdRef.detectChanges();
  }

  save() {
    this.isSubmit = true;
    this.chdRef.detectChanges();
    if (!this.bankForm.valid) return;

    const v = this.bankForm.value;
    const mangopayAddress = {
      addressLine1: v.addressLine1,
      city: v.city,
      region: v.region,
      postalCode: v.postalCode,
      country: v.countryCode
    };

    const payload: any = {
      wallet: this.walletId,
      displayName: v.displayName,
      countryCode: v.countryCode,
      iban: this.cleanIban(v.iban)
    };

    if (this.isIndividual) {
      payload.individualRecipient = {
        holderName: v.holderName,
        holderLastName: v.holderLastName,
        mangopayAddress
      };
    } else {
      payload.businessRecipient = {
        businessName: v.businessName,
        mangopayAddress
      };
    }

    this.sendXHR = 'primaryDark-disabled';
    this.isLoading = true;
    this.chdRef.detectChanges();
    this.movementsService.createRecipient(payload).subscribe(() => {
      this.isLoading = false;
      this.successOperation = true;
      this.chdRef.detectChanges();
    }, () => {
      this.isLoading = false;
      this.isSubmit = false;
      this.sendXHR = this.bankForm.valid ? 'primaryDark' : 'primaryDark-disabled';
      this.toastService.showToast(this.translate.instant('MOVEMENTS.MONEY-OUT.ERROR'), 'warning');
      this.chdRef.detectChanges();
    });
  }

  private getCountryList(): { id: string; name: string }[] {
    return [
      { id: 'ES', name: 'España' },
      { id: 'AD', name: 'Andorra' },
      { id: 'AT', name: 'Austria' },
      { id: 'BE', name: 'Bélgica' },
      { id: 'BG', name: 'Bulgaria' },
      { id: 'CH', name: 'Suiza' },
      { id: 'CY', name: 'Chipre' },
      { id: 'CZ', name: 'República Checa' },
      { id: 'DE', name: 'Alemania' },
      { id: 'DK', name: 'Dinamarca' },
      { id: 'EE', name: 'Estonia' },
      { id: 'FI', name: 'Finlandia' },
      { id: 'FR', name: 'Francia' },
      { id: 'GB', name: 'Reino Unido' },
      { id: 'GI', name: 'Gibraltar' },
      { id: 'GR', name: 'Grecia' },
      { id: 'HR', name: 'Croacia' },
      { id: 'HU', name: 'Hungría' },
      { id: 'IE', name: 'Irlanda' },
      { id: 'IS', name: 'Islandia' },
      { id: 'IT', name: 'Italia' },
      { id: 'LI', name: 'Liechtenstein' },
      { id: 'LT', name: 'Lituania' },
      { id: 'LU', name: 'Luxemburgo' },
      { id: 'LV', name: 'Letonia' },
      { id: 'MC', name: 'Mónaco' },
      { id: 'MT', name: 'Malta' },
      { id: 'NL', name: 'Países Bajos' },
      { id: 'NO', name: 'Noruega' },
      { id: 'PL', name: 'Polonia' },
      { id: 'PT', name: 'Portugal' },
      { id: 'RO', name: 'Rumanía' },
      { id: 'SE', name: 'Suecia' },
      { id: 'SI', name: 'Eslovenia' },
      { id: 'SK', name: 'Eslovaquia' },
      { id: 'SM', name: 'San Marino' },
    ];
  }

  cleanIban(value: string): string {
    return value.toUpperCase().replace(/\s/g, '').trim();
  }

  reopenModal() {
    this.reopenModalEvent.emit();
    this.chdRef.detectChanges();
  }
}
